import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GOAML_QUERY_KEYS } from 'app/modules/goAML/queries/keys';
import {
  GoAMLAttachments,
  GoAMLAttachmentsResult,
  GoAMLFilingDetails,
} from 'app/modules/goAML/models';
import {
  addGoAMLAttachments,
  deleteGoAMLAttachment,
} from 'app/shared/api/sars';
import { sendErrorToast, sendSuccessToast } from 'app/shared/toasts/actions';
import { getPatronusErrorMessage } from 'app/shared/utils/fetchr';
import { useDispatch } from 'react-redux';

export const useAddGoamlAttachments = (filingId: number) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: GoAMLAttachments) => addGoAMLAttachments(payload),
    onSuccess: (result: GoAMLAttachmentsResult) => {
      queryClient.setQueryData<GoAMLFilingDetails>(
        GOAML_QUERY_KEYS.filing(filingId),
        (prev) =>
          prev
            ? {
                ...prev,
                attachments: [
                  ...(prev.attachments ?? []),
                  ...result.attachments,
                ],
              }
            : prev,
      );
      dispatch(
        sendSuccessToast('Successfully attached file to GoAML STR form.'),
      );
    },
    onError: async (error) => {
      dispatch(
        sendErrorToast(
          await getPatronusErrorMessage(
            error,
            'Failed to upload attachment. Please try again.',
          ),
        ),
      );
    },
  });
};

export const useDeleteGoamlAttachment = (filingId: number) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: number) =>
      deleteGoAMLAttachment(filingId, attachmentId),
    onSuccess: (_, attachmentId) => {
      queryClient.setQueryData<GoAMLFilingDetails>(
        GOAML_QUERY_KEYS.filing(filingId),
        (prev) =>
          prev
            ? {
                ...prev,
                attachments: (prev.attachments ?? []).filter(
                  (attachment) => attachment.id !== attachmentId,
                ),
              }
            : prev,
      );
      dispatch(
        sendSuccessToast('Successfully removed attachment from GoAML form.'),
      );
    },
    onError: async (error) => {
      dispatch(
        sendErrorToast(
          await getPatronusErrorMessage(
            error,
            'Failed to delete attachment. Please try again.',
          ),
        ),
      );
    },
  });
};
